import { supabase } from '../lib/supabase';

export const marketAnalysisService = {
  // Get market statistics using existing function
  async getMarketStats() {
    try {
      const { data, error } = await supabase?.rpc('get_property_market_stats');

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter au service d\'analyse. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la récupération des statistiques du marché:', error);
      throw error;
    }
  },

  // Get commune market trends
  async getCommuneMarketTrends(communeId, period = 'monthly') {
    try {
      const { data, error } = await supabase?.rpc('get_commune_market_trends', {
        commune_uuid: communeId,
        trend_period: period
      });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter au service d\'analyse. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la récupération des tendances:', error);
      throw error;
    }
  },

  // Compare commune markets
  async compareCommuneMarkets(communeIds) {
    try {
      const { data, error } = await supabase?.rpc('compare_commune_markets', {
        commune_ids_param: communeIds
      });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter au service d\'analyse. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la comparaison des marchés:', error);
      throw error;
    }
  },

  // Get all market analysis data
  async getMarketAnalysisData(filters = {}) {
    try {
      let query = supabase?.from('market_analysis_data')?.select(`
          *,
          commune:communes(id, name, district)
        `);

      if (filters?.communeId) {
        query = query?.eq('commune_id', filters?.communeId);
      }

      if (filters?.period) {
        query = query?.eq('period', filters?.period);
      }

      if (filters?.dateFrom) {
        query = query?.gte('analysis_date', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('analysis_date', filters?.dateTo);
      }

      query = query?.order('analysis_date', { ascending: false });

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la récupération des données d\'analyse:', error);
      throw error;
    }
  },

  // Save market comparison
  async saveMarketComparison(comparisonData) {
    try {
      const { data, error } = await supabase?.from('market_comparisons')?.insert([{
          comparison_name: comparisonData?.comparisonName,
          commune_ids: comparisonData?.communeIds,
          comparison_criteria: comparisonData?.comparisonCriteria,
          results_data: comparisonData?.resultsData,
          user_id: comparisonData?.userId
        }])?.select()?.single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la sauvegarde de la comparaison:', error);
      throw error;
    }
  },

  // Get user's saved comparisons
  async getUserComparisons(userId) {
    try {
      const { data, error } = await supabase?.from('market_comparisons')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la récupération des comparaisons:', error);
      throw error;
    }
  },

  // Get properties with market data
  async getPropertiesWithMarketData(filters = {}) {
    try {
      let query = supabase?.from('properties')?.select(`*,commune:communes(id, name, district, average_price,market_analysis_data!inner(average_price_per_sqm,price_change_percentage,rental_yield_average,market_score))`)?.eq('status', 'available');

      if (filters?.marketType) {
        query = query?.eq('market_type', filters?.marketType);
      }

      if (filters?.communeId) {
        query = query?.eq('commune_id', filters?.communeId);
      }

      if (filters?.priceMin) {
        query = query?.gte('price', filters?.priceMin);
      }

      if (filters?.priceMax) {
        query = query?.lte('price', filters?.priceMax);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la récupération des propriétés avec données de marché:', error);
      throw error;
    }
  },

  // Get price trends for heat map
  async getPriceTrendsHeatMap() {
    try {
      const { data, error } = await supabase?.from('communes')?.select(`
          id, name, district, average_price,
          market_analysis_data!inner(
            price_change_percentage,
            market_score,
            rental_yield_average
          )
        `);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        throw new Error('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause ou supprimé. Veuillez visiter votre tableau de bord Supabase pour vérifier le statut du projet.');
      }
      
      console.error('Erreur JS lors de la récupération des tendances de prix:', error);
      throw error;
    }
  }
};

export default marketAnalysisService;